import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  formatWordPressFieldCheckMissingLabels,
  getWordPressFieldCheckChip,
  getWordPressFieldCheckRequiredMissing,
  getWordPressFieldCheckSourceHint,
  getWordPressFieldCheckSourceLabel,
  getWordPressFieldCheckWarningMissing,
  groupWordPressFieldCheckMissingBySection,
} from './fieldCheckUi';
import type { WordPressFieldCheckBulkSummary } from './fieldCheckUi';
import type { WordPressFieldCheckMissingItem, WordPressFieldCheckResult } from './types';

export function FieldCheckListChip(props: {
  result: WordPressFieldCheckResult | null | undefined;
  sx?: SxProps<Theme>;
}) {
  const chip = getWordPressFieldCheckChip(props.result);
  if (!chip || !props.result) return null;

  return (
    <Tooltip title={`Saknas: ${formatWordPressFieldCheckMissingLabels(props.result.missing)}`}>
      <Chip
        size="small"
        label={chip.label}
        color={chip.color}
        variant={chip.color === 'error' ? 'filled' : 'outlined'}
        sx={props.sx}
      />
    </Tooltip>
  );
}

function MissingItemsBySection(props: { missing: WordPressFieldCheckMissingItem[] }) {
  return (
    <Stack spacing={1}>
      {groupWordPressFieldCheckMissingBySection(props.missing).map(group => (
        <Box key={group.section}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {group.section}
          </Typography>
          <Stack spacing={0.25} sx={{ mt: 0.25 }}>
            {group.items.map((item) => {
              const sourceLabel = getWordPressFieldCheckSourceLabel(item.source);
              const sourceHint = getWordPressFieldCheckSourceHint(item.source);
              return (
                <Stack key={`${item.kind}-${item.key}`} direction="row" spacing={0.75} alignItems="center">
                  {item.level === 'required'
                    ? <ErrorOutlineIcon color="error" sx={{ fontSize: 16 }} />
                    : <WarningAmberIcon color="warning" sx={{ fontSize: 16 }} />}
                  <Typography variant="body2">
                    {item.kind === 'group' ? `Minst ett av: ${item.label}` : item.label}
                  </Typography>
                  {item.level === 'required' && (
                    <Chip size="small" label="Obligatorisk" color="error" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                  {sourceLabel && (
                    <Tooltip title={sourceHint || ''}>
                      <Chip size="small" label={sourceLabel} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    </Tooltip>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

export function FieldCheckCard(props: {
  result: WordPressFieldCheckResult | null | undefined;
  isPublished: boolean;
  rechecking: boolean;
  onOpenAsset: () => void;
  onRecheck: () => void;
}) {
  const { result } = props;
  if (!result || result.status === 'not_configured') return null;

  const requiredMissing = getWordPressFieldCheckRequiredMissing(result);
  const hasIssues = result.missing.length > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: result.status === 'blocked' ? 'error.light' : result.status === 'warnings' ? 'warning.light' : 'divider',
      }}
    >
      <Stack spacing={1.25}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {result.status === 'complete' && <CheckCircleOutlineIcon color="success" fontSize="small" />}
            {result.status === 'warnings' && <WarningAmberIcon color="warning" fontSize="small" />}
            {result.status === 'blocked' && <ErrorOutlineIcon color="error" fontSize="small" />}
            <Typography variant="subtitle2">Fältkontroll</Typography>
            <Typography variant="body2" color="text.secondary">
              {result.passedCount} av {result.checkedCount} OK
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75}>
            {hasIssues && (
              <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={props.onOpenAsset}>
                Öppna assetdetaljer
              </Button>
            )}
            <Button size="small" startIcon={<RefreshIcon />} onClick={props.onRecheck} disabled={props.rechecking}>
              {props.rechecking ? 'Kontrollerar...' : 'Kontrollera igen'}
            </Button>
          </Stack>
        </Box>

        {result.status === 'complete' && (
          <Typography variant="body2" color="text.secondary">
            Alla kontrollerade fält är ifyllda.
          </Typography>
        )}

        {result.status === 'blocked' && (
          <Alert severity="error" sx={{ py: 0.25 }}>
            {props.isPublished
              ? `Posten är publicerad men obligatoriska fält är tomma: ${formatWordPressFieldCheckMissingLabels(requiredMissing)}. Fyll i fälten. Ändringar kan hämtas av WordPress automatiskt.`
              : `Posten kan inte publiceras förrän obligatoriska fält är ifyllda: ${formatWordPressFieldCheckMissingLabels(requiredMissing)}.`}
          </Alert>
        )}

        {hasIssues && <MissingItemsBySection missing={result.missing} />}
      </Stack>
    </Paper>
  );
}

export function FieldCheckPublishDialog(props: {
  open: boolean;
  itemTitle: string;
  result: WordPressFieldCheckResult | null | undefined;
  busy: boolean;
  onCancel: () => void;
  onOpenAsset: () => void;
  onConfirm: () => void;
}) {
  const warnings = getWordPressFieldCheckWarningMissing(props.result);

  return (
    <Dialog open={props.open} onClose={props.onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Tomma fält i {props.itemTitle}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Alert severity="warning" sx={{ py: 0.25 }}>
            {warnings.length === 1
              ? '1 fält som ska kontrolleras är tomt. Du kan publicera ändå.'
              : `${warnings.length} fält som ska kontrolleras är tomma. Du kan publicera ändå.`}
          </Alert>
          <MissingItemsBySection missing={warnings} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel} disabled={props.busy}>Avbryt</Button>
        <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={props.onOpenAsset} disabled={props.busy}>
          Gå och fyll i
        </Button>
        <Button variant="contained" onClick={props.onConfirm} disabled={props.busy}>
          Publicera ändå
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function FieldCheckBulkPublishDialog(props: {
  open: boolean;
  summary: WordPressFieldCheckBulkSummary | null;
  busy: boolean;
  onCancel: () => void;
  onPublishAllowed: () => void;
  onPublishCompleteOnly: () => void;
  children?: React.ReactNode;
}) {
  const completeCount = props.summary?.completeIds.length || 0;
  const warningCount = props.summary?.warningIds.length || 0;
  const blockedCount = props.summary?.blockedIds.length || 0;
  const total = completeCount + warningCount + blockedCount;
  const allowedCount = completeCount + warningCount;

  return (
    <Dialog open={props.open} onClose={props.onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Publicera {total} markerade poster</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleOutlineIcon color="success" fontSize="small" />
            <Typography variant="body2">{completeCount} kompletta</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberIcon color="warning" fontSize="small" />
            <Typography variant="body2">{warningCount} med tomma varningsfält (publiceras ändå)</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <ErrorOutlineIcon color="error" fontSize="small" />
            <Typography variant="body2">{blockedCount} med tomma obligatoriska fält (publiceras inte)</Typography>
          </Stack>
          {props.children}
          {allowedCount === 0 && (
            <Alert severity="error" sx={{ py: 0.25, mt: 1 }}>
              Ingen av de markerade posterna kan publiceras. Fyll i de obligatoriska fälten först.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel} disabled={props.busy}>Avbryt</Button>
        {warningCount > 0 && completeCount > 0 && (
          <Button variant="outlined" onClick={props.onPublishCompleteOnly} disabled={props.busy}>
            Publicera bara de {completeCount} kompletta
          </Button>
        )}
        <Button variant="contained" onClick={props.onPublishAllowed} disabled={props.busy || allowedCount === 0}>
          Publicera {allowedCount}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
